
import { useState, useEffect } from "react";
import { getConversation, getConversationAudio } from "@/services/ai/elevenLabsService/callStatusService";
import { toast } from "@/hooks/use-toast";

interface UseConversationDetailProps {
  conversationId: string;
}

export const useConversationDetail = ({ conversationId }: UseConversationDetailProps) => {
  const [conversationDetails, setConversationDetails] = useState<any>(null);
  const [isLoadingDetails, setIsLoadingDetails] = useState(true);
  const [audioData, setAudioData] = useState<any>(null);
  const [isLoadingAudio, setIsLoadingAudio] = useState(false);
  const [currentTab, setCurrentTab] = useState("details");

  // Load conversation details on mount
  useEffect(() => {
    const loadConversationDetails = async () => {
      setIsLoadingDetails(true);
      try {
        const result = await getConversation(conversationId);
        if (result.success) {
          setConversationDetails(result.conversation);
          if (result.audio) {
            setAudioData(result.audio);
          }
        } else {
          toast({
            title: "Error fetching conversation details",
            description: result.error,
            variant: "destructive"
          });
        }
      } catch (error) {
        console.error("Error fetching conversation details:", error);
        toast({
          title: "An unexpected error occurred",
          description: error instanceof Error ? error.message : "Failed to fetch conversation details",
          variant: "destructive"
        });
      } finally {
        setIsLoadingDetails(false);
      }
    };

    if (conversationId) {
      loadConversationDetails();
    }
  }, [conversationId]);

  const handleFetchAudio = async () => {
    if (!conversationId) return;
    
    setIsLoadingAudio(true);
    try {
      const result = await getConversationAudio(conversationId);
      if (result.success && result.audio) {
        setAudioData(result.audio);
        setCurrentTab("audio");
      } else {
        toast({
          title: "Error fetching audio",
          description: result.error || "No audio available for this conversation",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error("Error fetching conversation audio:", error);
      toast({
        title: "Error fetching audio",
        description: error instanceof Error ? error.message : "An unknown error occurred",
        variant: "destructive"
      });
    } finally {
      setIsLoadingAudio(false);
    }
  };

  return {
    conversationDetails,
    isLoadingDetails,
    audioData,
    isLoadingAudio,
    currentTab,
    setCurrentTab,
    handleFetchAudio
  };
};
